import { useRef, useState } from 'react';
import { Toaster } from '@knockrentals/knock-react';
import { useFeatureFlagContext } from '../../../../../Context/FeatureFlagContext';
import {
  updateRenterAIBulkSettings,
  updateRenterAISettings,
} from '../../../VirtualAgentAPI';
import { editAiEmailEnabled } from '../../../aiEmailAPI';
import {
  ACTIVATION_STATUS,
  AI_LEASING,
  AI_RESIDENT,
  EMAIL_DISABLED_VALUE,
  LEASING_KEY,
  RESIDENT_KEY,
  TOAST_ERROR_MESSAGE,
  TOAST_SUCCESS_MESSAGE,
  UPDATE_TYPE,
} from './constants';

export function useProductSettingsHandlers({
  companyId,
  productsEnabled,
  setProductsEnabled,
}) {
  const { isUnifiedLeasingSettingsEnabled, isUnifiedResidentSettingsEnabled } =
    useFeatureFlagContext();
  const confirmationRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationMode, setConfirmationMode] = useState(null);
  const [filteredPropertyIds, setFilteredPropertyIds] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const leasingEnabledCount = productsEnabled.filter(
    (product) => product.leasingEnabled,
  ).length;
  const residentEnabledCount = productsEnabled.filter(
    (product) => product.residentEnabled,
  ).length;

  const products = [
    {
      key: LEASING_KEY,
      name: AI_LEASING,
      count: leasingEnabledCount,
    },
    {
      key: RESIDENT_KEY,
      name: AI_RESIDENT,
      count: residentEnabledCount,
    },
  ].filter(
    (product) =>
      !(isUnifiedLeasingSettingsEnabled && product.key === LEASING_KEY) &&
      !(isUnifiedResidentSettingsEnabled && product.key === RESIDENT_KEY),
  );

  const [selectedProduct, setSelectedProduct] = useState(products[0]?.key);

  const handleConfirmationCancel = () => {
    setConfirmationMode(null);
    confirmationRef.current = null;
  };

  const handleProductSelect = (productKey) => {
    setSelectedProperties([]);
    setSelectedProduct(productKey);
  };

  const showToaster = (error = false) => {
    const toasterClass = error
      ? Toaster.ToastClasses.error
      : Toaster.ToastClasses.success;
    const message = error ? TOAST_ERROR_MESSAGE : TOAST_SUCCESS_MESSAGE;
    Toaster.showToast(message, 2000, toasterClass);
  };

  const disableEmailSettings = async (propertiesIds) => {
    setProductsEnabled((prev) =>
      prev.map((row) =>
        propertiesIds.includes(row.propertyId)
          ? {
              ...row,
              aiEmailEnabled: EMAIL_DISABLED_VALUE,
            }
          : row,
      ),
    );

    await Promise.all(
      propertiesIds.map((propertyId) =>
        editAiEmailEnabled([
          {
            property_id: propertyId,
            company_id: companyId,
            ai_email_enabled: EMAIL_DISABLED_VALUE,
          },
        ]),
      ),
    );
  };

  const updateSingleSetting = async (propertyId, checked) => {
    setIsLoading(true);
    setProductsEnabled((prev) =>
      prev.map((row) =>
        row.propertyId === propertyId
          ? { ...row, [`${selectedProduct}Enabled`]: checked }
          : row,
      ),
    );

    const productKey = `ai_${selectedProduct}`;
    const payload = {
      company_id: companyId,
      property_id: propertyId,
      [productKey]: checked
        ? ACTIVATION_STATUS.ACTIVE
        : ACTIVATION_STATUS.INACTIVE,
    };
    try {
      await updateRenterAISettings(payload);
      if (!checked && selectedProduct === LEASING_KEY) {
        await disableEmailSettings([propertyId]);
      }
      setSelectedProperties([]);
      showToaster();
    } catch (e) {
      showToaster(true);
    }
    setIsLoading(false);
  };

  const updateBulkSettings = async (activationStatus) => {
    setIsLoading(true);
    const isEnabled = activationStatus === ACTIVATION_STATUS.ACTIVE;

    setProductsEnabled((prev) =>
      prev.map((row) =>
        selectedProperties.includes(row.propertyId)
          ? { ...row, [`${selectedProduct}Enabled`]: isEnabled }
          : row,
      ),
    );

    const productKey = `ai_${selectedProduct}`;
    const payload = {
      company_id: companyId,
      [productKey]: activationStatus,
      properties: selectedProperties,
    };
    try {
      await updateRenterAIBulkSettings(payload);
      if (!isEnabled && selectedProduct === LEASING_KEY) {
        await disableEmailSettings(selectedProperties);
      }
      setSelectedProperties([]);
      showToaster();
    } catch (e) {
      showToaster(true);
    }
    setIsLoading(false);
  };

  const handleSettingsUpdate = (propertyId, checked) => {
    if (!checked) {
      confirmationRef.current = {
        propertyId,
      };
      setConfirmationMode(UPDATE_TYPE.SINGLE);
    } else {
      updateSingleSetting(propertyId, true);
    }
  };

  const handleBulkSettingsUpdate = (activationStatus) => {
    if (activationStatus === ACTIVATION_STATUS.INACTIVE) {
      setConfirmationMode(UPDATE_TYPE.BULK);
    } else {
      updateBulkSettings(ACTIVATION_STATUS.ACTIVE);
    }
  };

  const handleConfirmation = () => {
    if (confirmationMode === UPDATE_TYPE.SINGLE) {
      const { propertyId } = confirmationRef.current;
      updateSingleSetting(propertyId, false);
    }
    if (confirmationMode === UPDATE_TYPE.BULK) {
      updateBulkSettings(ACTIVATION_STATUS.INACTIVE);
    }
    handleConfirmationCancel();
  };

  const handleFilterProperties = (propertyIds) => {
    const isEveryPropertiesSelected =
      propertyIds.length === productsEnabled.length;
    setFilteredPropertyIds(isEveryPropertiesSelected ? [] : propertyIds);
    setSelectedProperties([]);
  };

  const filterProperties = () => {
    const filteredIdMap = filteredPropertyIds.reduce((filterMap, id) => {
      filterMap[id] = true;
      return filterMap;
    }, {});
    return productsEnabled.filter(
      (property) => filteredIdMap[property.propertyId],
    );
  };

  const filteredProperties = filteredPropertyIds.length
    ? filterProperties()
    : productsEnabled;

  return {
    confirmationMode,
    filteredProperties,
    filteredPropertyIds,
    handleBulkSettingsUpdate,
    handleConfirmation,
    handleConfirmationCancel,
    handleFilterProperties,
    handleProductSelect,
    handleSettingsUpdate,
    isLoading,
    products,
    selectedProduct,
    selectedProperties,
    setSelectedProperties,
  };
}
